<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import {
  ArrowLeft,
  CheckCircle2,
  Clipboard,
  Clock3,
  KeyRound,
  LogIn,
  LogOut,
  RotateCcwKey,
  ShieldAlert,
  Trash2,
  UserRoundCheck,
  XCircle
} from "@lucide/vue";
import {
  callWorkbenchApi,
  fetchConfig,
  type WorkbenchApiResult,
  type WorkbenchCurrentUserData,
  type WorkbenchLoginData,
  type RuntimeConfig
} from "../services/api";

interface RequestRecord {
  id: number;
  method: string;
  path: string;
  requestedAt: string;
  durationMs: number;
  result: WorkbenchApiResult;
}

const TOKEN_STORAGE_KEY = "workbench_test_access_token";
const phone = ref("");
const password = ref("");
const platform = ref("macos");
const appVersion = ref("0.1.1");
const currentPassword = ref("");
const newPassword = ref("");
const resetPhone = ref("");
const accessToken = ref(localStorage.getItem(TOKEN_STORAGE_KEY) ?? "");
const isAdmin = ref<boolean | null>(null);
const busyAction = ref("");
const requestRecords = ref<RequestRecord[]>([]);
const runtimeConfig = ref<RuntimeConfig | null>(null);
let requestSequence = 0;

const latestRecord = computed(() => requestRecords.value[0]);
const hasToken = computed(() => accessToken.value.trim() !== "");
const identityLabel = computed(() => {
  if (!hasToken.value) {
    return "未登录";
  }
  if (isAdmin.value === true) {
    return "管理员";
  }
  if (isAdmin.value === false) {
    return "普通用户";
  }
  return "待校验";
});
const modeNotice = computed(() => {
  if (runtimeConfig.value?.workbench.authMode === "memory") {
    return `本地内存调试模式 · 真实校验空间 ID ${runtimeConfig.value.workbench.targetSpaceId} 权益`;
  }
  if (runtimeConfig.value?.workbench.authMode === "persistent") {
    return "应用库持久化认证模式";
  }
  return "认证存储未启用，登录接口将返回 503";
});

onMounted(async () => {
  try {
    runtimeConfig.value = await fetchConfig();
  } catch {
    runtimeConfig.value = null;
  }
});

async function testLogin(): Promise<void> {
  await runRequest<WorkbenchLoginData>("login", "POST", "/login", {
    body: {
      phone: phone.value,
      password: password.value,
      client: {
        platform: platform.value,
        app_version: appVersion.value
      }
    },
    onSuccess(data) {
      if (data?.access_token) {
        setToken(data.access_token);
        isAdmin.value = data.user.is_admin;
      }
    }
  });
}

async function testMe(): Promise<void> {
  await runRequest<WorkbenchCurrentUserData>("me", "GET", "/me", {
    tokenRequired: true,
    onSuccess(data) {
      if (data) {
        isAdmin.value = data.user.is_admin;
      }
    }
  });
}

async function testChangePassword(): Promise<void> {
  await runRequest("change-password", "POST", "/change-password", {
    tokenRequired: true,
    body: {
      current_password: currentPassword.value,
      new_password: newPassword.value
    }
  });
}

async function testLogout(): Promise<void> {
  await runRequest("logout", "POST", "/logout", {
    tokenRequired: true,
    onSuccess() {
      clearToken();
    }
  });
}

async function testAdminResetPassword(): Promise<void> {
  const targetPhone = resetPhone.value.trim();
  const confirmed = window.confirm(
    `确认将手机号 ${targetPhone} 的工作台密码重置为手机号后六位吗？重置后该用户的全部历史会话都会失效。`
  );
  if (!confirmed) {
    return;
  }

  await runRequest("admin-reset-password", "POST", "/admin/reset-password", {
    tokenRequired: true,
    body: { phone: targetPhone }
  });
}

async function runRequest<T = unknown>(
  action: string,
  method: string,
  path: string,
  options: {
    tokenRequired?: boolean;
    body?: Record<string, unknown>;
    onSuccess?: (data: T | null) => void;
  } = {}
): Promise<void> {
  if (options.tokenRequired && !hasToken.value) {
    appendClientError(method, path, "请先登录或粘贴 Access Token");
    return;
  }

  busyAction.value = action;
  const startedAt = performance.now();
  try {
    const result = await callWorkbenchApi<T>(path, {
      method,
      ...(options.body ? {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(options.body)
      } : {})
    }, options.tokenRequired ? accessToken.value.trim() : undefined);

    requestRecords.value.unshift({
      id: ++requestSequence,
      method,
      path,
      requestedAt: new Date().toLocaleTimeString("zh-CN", { hour12: false }),
      durationMs: Math.round(performance.now() - startedAt),
      result
    });
    if (result.ok) {
      options.onSuccess?.(result.body.data);
    }
  } catch (error) {
    appendClientError(method, path, error instanceof Error ? error.message : "网络请求失败");
  } finally {
    busyAction.value = "";
  }
}

function appendClientError(method: string, path: string, message: string): void {
  requestRecords.value.unshift({
    id: ++requestSequence,
    method,
    path,
    requestedAt: new Date().toLocaleTimeString("zh-CN", { hour12: false }),
    durationMs: 0,
    result: {
      ok: false,
      status: 0,
      body: { code: "CLIENT_ERROR", message, data: null }
    }
  });
}

function setToken(token: string): void {
  accessToken.value = token;
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

function syncToken(): void {
  const token = accessToken.value.trim();
  // 手动替换 Token 后必须重新调用 /me，不能沿用上一个账号的管理员标记。
  isAdmin.value = null;
  if (token) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

function clearToken(): void {
  accessToken.value = "";
  isAdmin.value = null;
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

async function copyToken(): Promise<void> {
  if (hasToken.value) {
    await navigator.clipboard.writeText(accessToken.value.trim());
  }
}

function formatResponse(record: RequestRecord | undefined): string {
  return record ? JSON.stringify(record.result.body, null, 2) : "// 调用任意接口后，完整响应将显示在这里";
}
</script>

<template>
  <main class="test-shell">
    <header class="test-header">
      <div>
        <RouterLink class="back-link" to="/">
          <ArrowLeft :size="16" />
          返回框架首页
        </RouterLink>
        <p class="eyebrow">Workbench API Console</p>
        <h1>生命教练工作台接口测试</h1>
        <p class="description">按真实登录顺序验证认证与管理员接口，页面会自动保存成功登录后的 Token。</p>
      </div>
      <div class="mode-badge">
        <ShieldAlert :size="18" />
        {{ modeNotice }}
      </div>
    </header>

    <section class="status-strip">
      <div>
        <span class="status-label">API 前缀</span>
        <code>/api/workbench/v1/auth</code>
      </div>
      <div>
        <span class="status-label">Token</span>
        <strong :class="hasToken ? 'success-text' : 'muted-text'">{{ hasToken ? "已保存" : "未登录" }}</strong>
      </div>
      <div>
        <span class="status-label">当前身份</span>
        <strong :class="isAdmin === true ? 'success-text' : 'muted-text'">{{ identityLabel }}</strong>
      </div>
      <div>
        <span class="status-label">最后请求</span>
        <strong v-if="latestRecord" :class="latestRecord.result.ok ? 'success-text' : 'error-text'">
          HTTP {{ latestRecord.result.status || "-" }}
        </strong>
        <strong v-else class="muted-text">未调用</strong>
      </div>
    </section>

    <section class="test-grid">
      <div class="forms-column">
        <article class="test-card">
          <div class="card-heading">
            <span class="step-number">1</span>
            <div>
              <h2>手机号密码登录</h2>
              <code>POST /login</code>
            </div>
            <LogIn :size="21" />
          </div>
          <form class="form-grid" @submit.prevent="testLogin">
            <label>
              <span>手机号</span>
              <input
                v-model="phone"
                inputmode="numeric"
                minlength="6"
                maxlength="15"
                pattern="[0-9]{6,15}"
                placeholder="输入小程序绑定手机号（支持海外号码）"
                required
              />
            </label>
            <label>
              <span>密码</span>
              <input v-model="password" type="password" placeholder="首次为手机号后六位" required />
            </label>
            <label>
              <span>客户端平台</span>
              <select v-model="platform">
                <option value="macos">macOS</option>
                <option value="windows">Windows</option>
              </select>
            </label>
            <label>
              <span>应用版本</span>
              <input v-model="appVersion" placeholder="0.1.1" />
            </label>
            <button class="primary-button full-width" type="submit" :disabled="Boolean(busyAction)">
              <LogIn :size="17" />
              {{ busyAction === "login" ? "请求中…" : "调用登录接口" }}
            </button>
          </form>
        </article>

        <article class="test-card">
          <div class="card-heading">
            <span class="step-number">2</span>
            <div>
              <h2>当前用户与使用权</h2>
              <code>GET /me</code>
            </div>
            <UserRoundCheck :size="21" />
          </div>
          <button class="secondary-button full-width" type="button" :disabled="Boolean(busyAction)" @click="testMe">
            <UserRoundCheck :size="17" />
            {{ busyAction === "me" ? "请求中…" : "重新校验 Token 与权益" }}
          </button>
        </article>

        <article class="test-card">
          <div class="card-heading">
            <span class="step-number">3</span>
            <div>
              <h2>修改登录密码</h2>
              <code>POST /change-password</code>
            </div>
            <KeyRound :size="21" />
          </div>
          <form class="form-grid" @submit.prevent="testChangePassword">
            <label>
              <span>当前密码</span>
              <input v-model="currentPassword" type="password" autocomplete="current-password" required />
            </label>
            <label>
              <span>新密码</span>
              <input v-model="newPassword" type="password" autocomplete="new-password" minlength="8" maxlength="64" required />
            </label>
            <button class="secondary-button full-width" type="submit" :disabled="Boolean(busyAction)">
              <KeyRound :size="17" />
              {{ busyAction === "change-password" ? "请求中…" : "调用修改密码接口" }}
            </button>
          </form>
        </article>

        <article class="test-card">
          <div class="card-heading">
            <span class="step-number">4</span>
            <div>
              <h2>管理员重置密码</h2>
              <code>POST /admin/reset-password</code>
            </div>
            <RotateCcwKey :size="21" />
          </div>
          <form class="form-grid" @submit.prevent="testAdminResetPassword">
            <label class="full-width">
              <span>目标用户手机号</span>
              <input
                v-model="resetPhone"
                inputmode="numeric"
                minlength="6"
                maxlength="15"
                pattern="[0-9]{6,15}"
                placeholder="输入需要重置密码的绑定手机号"
                required
              />
            </label>
            <p class="admin-tip full-width">
              需要管理员 Token。成功后密码恢复为手机号后六位，目标用户全部历史会话立即失效。
            </p>
            <button class="danger-button full-width" type="submit" :disabled="Boolean(busyAction)">
              <RotateCcwKey :size="17" />
              {{ busyAction === "admin-reset-password" ? "请求中…" : "确认并调用重置接口" }}
            </button>
          </form>
        </article>

        <article class="test-card">
          <div class="card-heading">
            <span class="step-number">5</span>
            <div>
              <h2>退出登录</h2>
              <code>POST /logout</code>
            </div>
            <LogOut :size="21" />
          </div>
          <button class="danger-button full-width" type="button" :disabled="Boolean(busyAction)" @click="testLogout">
            <LogOut :size="17" />
            {{ busyAction === "logout" ? "请求中…" : "撤销当前会话" }}
          </button>
        </article>
      </div>

      <aside class="console-column">
        <article class="test-card sticky-card">
          <div class="card-heading compact-heading">
            <div>
              <h2>Access Token</h2>
              <p>可自动写入，也可手动粘贴调试。</p>
            </div>
            <div class="icon-actions">
              <button type="button" title="复制 Token" :disabled="!hasToken" @click="copyToken"><Clipboard :size="16" /></button>
              <button type="button" title="清除 Token" :disabled="!hasToken" @click="clearToken"><Trash2 :size="16" /></button>
            </div>
          </div>
          <textarea v-model="accessToken" class="token-input" rows="4" placeholder="登录成功后自动填入" @change="syncToken" />

          <div class="response-heading">
            <div>
              <h2>最后响应</h2>
              <p v-if="latestRecord" class="request-meta">
                <Clock3 :size="14" />
                {{ latestRecord.requestedAt }} · {{ latestRecord.method }} {{ latestRecord.path }} · {{ latestRecord.durationMs }}ms
              </p>
            </div>
            <span v-if="latestRecord" class="response-status" :class="latestRecord.result.ok ? 'response-ok' : 'response-error'">
              <CheckCircle2 v-if="latestRecord.result.ok" :size="15" />
              <XCircle v-else :size="15" />
              {{ latestRecord.result.status || "CLIENT" }}
            </span>
          </div>
          <pre class="response-code">{{ formatResponse(latestRecord) }}</pre>

          <div v-if="requestRecords.length > 1" class="history-list">
            <div class="history-title">
              <span>请求历史</span>
              <button type="button" @click="requestRecords = []">清空</button>
            </div>
            <button
              v-for="record in requestRecords.slice(1, 6)"
              :key="record.id"
              class="history-item"
              type="button"
              @click="requestRecords = [record, ...requestRecords.filter((item) => item.id !== record.id)]"
            >
              <span>{{ record.method }} {{ record.path }}</span>
              <strong :class="record.result.ok ? 'success-text' : 'error-text'">{{ record.result.status || "-" }}</strong>
            </button>
          </div>
        </article>
      </aside>
    </section>
  </main>
</template>

<style scoped>
.test-shell {
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
  padding: 28px 0 56px;
}

.test-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
  color: #475569;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
}

.description {
  max-width: 680px;
  margin-top: 10px;
  color: #64748b;
  line-height: 1.7;
}

.mode-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 320px;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 11px 13px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
}

.status-strip {
  display: grid;
  grid-template-columns: 1.4fr 0.65fr 0.75fr 0.65fr;
  gap: 1px;
  overflow: hidden;
  margin-bottom: 18px;
  border: 1px solid #dfe5ee;
  border-radius: 8px;
  background: #dfe5ee;
}

.status-strip > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-width: 0;
  padding: 13px 15px;
  background: #ffffff;
}

.status-strip code {
  overflow: hidden;
  color: #334155;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-label {
  color: #64748b;
  font-size: 13px;
}

.test-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(380px, 1.05fr);
  align-items: start;
  gap: 18px;
}

.forms-column {
  display: grid;
  gap: 14px;
}

.test-card {
  min-width: 0;
  border: 1px solid #dfe5ee;
  border-radius: 8px;
  padding: 18px;
  background: #ffffff;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
}

.sticky-card {
  position: sticky;
  top: 18px;
}

.card-heading {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.card-heading > svg {
  color: #2563eb;
}

.card-heading code {
  display: inline-block;
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
}

.compact-heading {
  grid-template-columns: 1fr auto;
}

.compact-heading p,
.response-heading p {
  margin-top: 5px;
  color: #64748b;
  font-size: 12px;
}

.step-number {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 800;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 13px;
}

label {
  display: grid;
  gap: 7px;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
}

input,
select,
textarea {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  outline: none;
  background: #ffffff;
  color: #0f172a;
  transition: border-color 0.15s, box-shadow 0.15s;
}

input,
select {
  height: 40px;
  padding: 0 11px;
}

textarea {
  padding: 11px;
  resize: vertical;
}

input:focus,
select:focus,
textarea:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.primary-button,
.secondary-button,
.danger-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  border-radius: 7px;
  padding: 0 14px;
  cursor: pointer;
  font-weight: 750;
}

.primary-button {
  border: 1px solid #1d4ed8;
  background: #2563eb;
  color: #ffffff;
}

.secondary-button {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #1e293b;
}

.danger-button {
  border: 1px solid #fecaca;
  background: #fff1f2;
  color: #be123c;
}

.primary-button:disabled,
.secondary-button:disabled,
.danger-button:disabled {
  cursor: wait;
  opacity: 0.55;
}

.full-width {
  grid-column: 1 / -1;
  width: 100%;
}

.admin-tip {
  margin: 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.6;
}

.icon-actions {
  display: flex;
  gap: 6px;
}

.icon-actions button {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 1px solid #d8dee8;
  border-radius: 7px;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
}

.icon-actions button:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.token-input {
  min-height: 92px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.6;
}

.response-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 22px 0 10px;
}

.request-meta {
  display: flex;
  align-items: center;
  gap: 5px;
}

.response-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 999px;
  padding: 5px 9px;
  font-size: 12px;
  font-weight: 800;
}

.response-ok {
  background: #ecfdf5;
  color: #047857;
}

.response-error {
  background: #fff1f2;
  color: #be123c;
}

.response-code {
  min-height: 280px;
  max-height: 480px;
  overflow: auto;
  margin: 0;
  border-radius: 7px;
  padding: 15px;
  background: #0f172a;
  color: #dbeafe;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.65;
  white-space: pre-wrap;
  word-break: break-word;
}

.history-list {
  margin-top: 18px;
  border-top: 1px solid #e2e8f0;
  padding-top: 14px;
}

.history-title,
.history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.history-title {
  margin-bottom: 6px;
  color: #475569;
  font-size: 13px;
  font-weight: 800;
}

.history-title button {
  border: 0;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  font-size: 12px;
}

.history-item {
  width: 100%;
  border: 0;
  border-radius: 6px;
  padding: 8px;
  background: transparent;
  color: #475569;
  cursor: pointer;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  text-align: left;
}

.history-item:hover {
  background: #f8fafc;
}

.success-text { color: #047857; }
.error-text { color: #be123c; }
.muted-text { color: #94a3b8; }

@media (max-width: 860px) {
  .test-header {
    align-items: stretch;
    flex-direction: column;
  }

  .mode-badge {
    max-width: none;
  }

  .status-strip,
  .test-grid {
    grid-template-columns: 1fr;
  }

  .sticky-card {
    position: static;
  }
}

@media (max-width: 560px) {
  .test-shell {
    width: min(100% - 24px, 1180px);
    padding-top: 18px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
